import { Box, Flex, Heading, VStack } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { ROUTES } from 'router/routes';
import { User, ShoppingBag } from 'lucide-react';
import { KeyboardEvent } from 'react';
import { useEventTracking } from 'hooks/useEventTracking';

interface ProfileNavigationProps {
  currentPathname: string;
  variant?: 'mobile' | 'desktop';
}

interface NavItem {
  id: string;
  route: string;
  title: JSX.Element;
  icon: typeof User;
}

const ProfileTabCard = ({
  title,
  icon: IconComponent,
  isSelected,
  ariaLabel,
}: {
  title: JSX.Element;
  icon: typeof User;
  isSelected: boolean;
  ariaLabel: string;
}) => {
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
    }
  };
  return (
    <Box
      as='button'
      bg={isSelected ? 'green.G500' : { base: 'white', md: 'white' }}
      color={isSelected ? 'white' : 'dark.D900'}
      p={{ base: '10px', md: 6 }}
      borderRadius={{ base: '50%', md: '16px' }}
      borderWidth='1px'
      borderColor={isSelected ? 'green.G500' : { base: 'dark.D150', md: 'dark.D100' }}
      boxShadow={isSelected ? { base: 'sm', md: 'sm' } : { base: 'none', md: 'sm' }}
      _hover={{
        borderColor: 'green.G500',
        bg: {
          base: isSelected ? 'green.G500' : 'green.G0',
          md: isSelected ? 'green.G500' : 'white',
        },
        transform: { base: 'scale(1.05)', md: 'translateY(-2px)' },
        boxShadow: { base: 'sm', md: 'md' },
      }}
      _active={{
        bg: { base: isSelected ? 'green.G700' : 'green.G50', md: 'white' },
        transform: { base: 'scale(0.95)', md: 'translateY(-2px)' },
      }}
      transition='all 0.2s ease'
      cursor='pointer'
      tabIndex={0}
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      width={{ base: '56px', md: '100%' }}
      height={{ base: '56px', md: 'auto' }}
      minH={{ base: '56px', md: '120px' }}
      flexShrink={0}
      position='relative'
      display='flex'
      alignItems='center'
      justifyContent='center'
    >
      {/* Mobile: Icon Only */}
      <Box
        fontSize={{ base: '24px', md: '32px' }}
        flexShrink={0}
        opacity={isSelected ? 1 : 0.8}
        transition='opacity 0.2s ease'
        display={{ base: 'flex', md: 'none' }}
        alignItems='center'
        justifyContent='center'
      >
        <IconComponent size={24} />
      </Box>

      {/* Desktop: Full Card Content */}
      <Flex
        direction='column'
        align='center'
        justify='center'
        gap={2}
        height='100%'
        display={{ base: 'none', md: 'flex' }}
      >
        <Box
          fontSize='32px'
          flexShrink={0}
          opacity={isSelected ? 1 : 0.8}
          transition='opacity 0.2s ease'
        >
          <IconComponent size={32} />
        </Box>
        <Heading
          variant='headingMedium'
          fontSize='xl'
          fontWeight='600'
          lineHeight='1.2'
          textAlign='center'
        >
          {title}
        </Heading>
      </Flex>
    </Box>
  );
};

export const ProfileNavigation = ({ currentPathname }: ProfileNavigationProps) => {
  const navItems: NavItem[] = [
    {
      id: 'profile',
      route: ROUTES.profile,
      title: <FormattedMessage id='profile.generalInformation' defaultMessage='المعلومات العامة' />,
      icon: User,
    },
    {
      id: 'history',
      route: ROUTES.history,
      title: <FormattedMessage id='profile.orderHistory' defaultMessage='سجل الطلبات' />,
      icon: ShoppingBag,
    },
  ];

  const { eventTracking } = useEventTracking();

  return (
    <>
      {/* Mobile: Horizontal Tabs with Icons */}
      <Box
        display={{ base: 'block', lg: 'none' }}
        overflowX='auto'
        overflowY='hidden'
        pb={4}
        px={{ base: 3, sm: 4 }}
        sx={{
          '&::-webkit-scrollbar': {
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'dark.D200',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'dark.D300',
          },
          scrollbarWidth: 'thin',
          scrollbarColor: 'dark.D200 transparent',
        }}
        dir='rtl'
      >
        <Flex
          direction='row'
          gap={{ base: 3, sm: 4 }}
          justifyContent='space-between'
          alignItems='flex-start'
          py={2}
          dir='rtl'
          width='100%'
        >
          {navItems.map((item) => {
            const isSelected = currentPathname === item.route;
            const isHistoryTab = item.route === ROUTES.history;
            return (
              <Flex key={item.id} direction='column' align='center' gap={2} flex={1} minW='60px'>
                <Link
                  to={item.route}
                  style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                  onClick={() =>
                    isHistoryTab
                      ? eventTracking.orderHistory.clicked()
                      : eventTracking.profile.pageClicked()
                  }
                >
                  <ProfileTabCard
                    title={item.title}
                    icon={item.icon}
                    isSelected={isSelected}
                    ariaLabel={`Navigate to ${item.id}`}
                  />
                </Link>
                <Heading
                  variant='headingSmall'
                  fontSize='xs'
                  fontWeight='600'
                  textAlign='center'
                  color={isSelected ? 'green.G500' : 'dark.D700'}
                  lineHeight='1.4'
                  noOfLines={2}
                  p={1}
                  mt={0.5}
                >
                  {item.title}
                </Heading>
              </Flex>
            );
          })}
        </Flex>
      </Box>

      {/* Desktop: Vertical Grid Layout */}
      <VStack display={{ base: 'none', lg: 'flex' }} spacing={5} align='stretch' dir='rtl' px={2}>
        {navItems.map((item) => {
          const isSelected = currentPathname === item.route;
          const isHistoryTab = item.route === ROUTES.history;
          return (
            <Link
              key={item.id}
              to={item.route}
              onClick={() =>
                isHistoryTab
                  ? eventTracking.orderHistory.clicked()
                  : eventTracking.profile.pageClicked()
              }
            >
              <ProfileTabCard
                title={item.title}
                icon={item.icon}
                isSelected={isSelected}
                ariaLabel={`Navigate to ${item.id}`}
              />
            </Link>
          );
        })}
      </VStack>
    </>
  );
};
